import { useAuth0 } from '@auth0/auth0-react'
import { Menu, MenuItem } from '@progress/kendo-react-layout'
import {
  type MenuItemModel,
  type MenuItemProps,
} from '@progress/kendo-react-layout'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { useAvailableApplications } from '~/hooks/useAvailableApplications'
import { useCurrentApplication } from '~/hooks/useCurrentApplication'
import { localeCodeMap } from '~/locales'
import { exists, type IconName } from '~/utils'

import styles from './HeaderMenu.css'
import { MenuItemRenderContent } from './MenuItemRenderContent'

type MenuProps = React.ComponentProps<typeof Menu>

type MenuItemWithId = Omit<MenuItemModel, 'items'> & {
  id: string
  items?: MenuItemWithId[]
}

type ItemRenderParams = {
  item: Omit<MenuItemProps, 'data'> & {
    data: {
      href?: string
      icon?: IconName
      isActive?: boolean
      to?: string
    }
  }
  itemId: string
}

const itemRender = ({ item: { data, text }, itemId }: ItemRenderParams) => {
  if (exists(data.href)) {
    return (
      <a
        className={styles.anchor}
        href={data.href}
        rel="noreferrer"
        target="_blank"
      >
        <MenuItemRenderContent item={{ data, text }} itemId={itemId} />
      </a>
    )
  }

  return <MenuItemRenderContent item={{ data, text }} itemId={itemId} />
}

const linkRender = ({ item: { data, text }, itemId }: ItemRenderParams) => {
  if (exists(data.to)) {
    return (
      <Link className={styles.subItemLink} to={data.to}>
        <MenuItemRenderContent item={{ data, text }} itemId={itemId} />
      </Link>
    )
  }

  return <MenuItemRenderContent item={{ data, text }} itemId={itemId} />
}

export const HeaderMenu = () => {
  const { logout } = useAuth0()
  const handleLogoutClick = React.useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } })
  }, [logout])

  const { i18n, t } = useTranslation()

  const availableApplications = useAvailableApplications()
  const currentApplication = useCurrentApplication()
  const items = React.useMemo<MenuItemWithId[]>(
    () => [
      {
        id: 'manual',
        data: { href: 'https://docs.9segs.com/', icon: 'help' },
        text: t('help'),
      },
      {
        id: 'language',
        data: { icon: 'globe' },
        items: [
          {
            id: 'language-ja',
            data: {
              isActive: i18n.language === localeCodeMap.jaJp,
              onClick: () => i18n.changeLanguage(localeCodeMap.jaJp),
            },
            text: t('japanese'),
          },
          {
            id: 'language-en',
            data: {
              isActive: i18n.language === localeCodeMap.enUs,
              onClick: () => i18n.changeLanguage(localeCodeMap.enUs),
            },
            text: 'English',
          },
        ],
        text: 'Language',
      },
      {
        id: 'logout',
        data: { icon: 'user' },
        items: [
          {
            id: 'logout-click',
            data: { onClick: handleLogoutClick },
            text: t('logout'),
          },
        ],
      },
      {
        id: 'applications',
        data: { icon: 'group' },
        items: availableApplications.map((application) => ({
          id: `applications-${application.id}`,
          data: {
            isActive: application.id === currentApplication?.id,
            to: application.rootPath,
          },
          text: application.name,
        })),
      },
    ],
    [availableApplications, currentApplication?.id, handleLogoutClick, i18n, t],
  )

  const handleSelect = React.useCallback<EH<MenuProps, 'onSelect'>>((event) => {
    event.item.data?.onClick?.(event)
  }, [])

  return (
    <Menu
      className={styles.root}
      itemRender={itemRender}
      onSelect={handleSelect}
    >
      {items?.map((item) => {
        if (exists(item.items)) {
          return (
            <MenuItem key={item.id} data={item.data} text={item.text}>
              {item.items.map((subitem) => (
                <MenuItem
                  key={subitem.id}
                  data={subitem.data}
                  linkRender={exists(subitem.data?.to) ? linkRender : undefined}
                  text={subitem.text}
                />
              ))}
            </MenuItem>
          )
        }

        return <MenuItem key={item.id} data={item.data} text={item.text} />
      })}
    </Menu>
  )
}
